import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FileSignature, Hourglass, AlertTriangle, Truck, ChevronRight } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { EMPTY_ARRAY } from '../../store/stableEmpties';
import { useTranslation } from '../../i18n';

/**
 * TenderPipelineWidget — encart « AO en cours » du Dashboard (15/06/2026,
 * demande utilisateur). Affiche les candidatures en attente, les
 * contre-offres à traiter et les AO gagnés à staffer, avec lien direct
 * vers la page Appels d'Offres. Rendu nul si aucun AO en cours.
 */
export default function TenderPipelineWidget() {
  const { t } = useTranslation();
  const activeApplications = useGameStore(s => s.activeApplications || EMPTY_ARRAY);
  const availableContracts = useGameStore(s => s.availableContracts || EMPTY_ARRAY);

  const stats = useMemo(() => ({
    pending: activeApplications.filter(a => a.stage === 'pending').length,
    counter: activeApplications.filter(a => a.stage === 'counter').length,
    staff: availableContracts.filter(c => c.tenderWon).length,
  }), [activeApplications, availableContracts]);

  if (stats.pending === 0 && stats.counter === 0 && stats.staff === 0) return null;

  const needsAction = stats.counter > 0 || stats.staff > 0;

  return (
    <Link
      to="/appels-offres"
      className={`block rounded-lg border px-4 py-3 transition-colors hover:border-accent/60 ${
        needsAction ? 'bg-amber-500/5 border-amber-500/40' : 'bg-ink-800 border-ink-600'
      }`}
      data-testid="dashboard-tender-widget"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs font-bold text-foreground flex items-center gap-2">
          <FileSignature className="w-4 h-4 text-emerald-300" />
          {t('ao_widget_title')}
        </p>
        <div className="flex items-center gap-4 text-[11px] font-mono">
          <span className="flex items-center gap-1 text-sky-300" data-testid="tender-widget-pending">
            <Hourglass className="w-3 h-3" /> {stats.pending} {t('ao_pipeline_pending')}
          </span>
          <span className={`flex items-center gap-1 ${stats.counter > 0 ? 'text-amber-300 font-bold' : 'text-slate-500'}`} data-testid="tender-widget-counter">
            <AlertTriangle className="w-3 h-3" /> {stats.counter} {t('ao_pipeline_counter')}
          </span>
          <span className={`flex items-center gap-1 ${stats.staff > 0 ? 'text-emerald-300 font-bold' : 'text-slate-500'}`} data-testid="tender-widget-staff">
            <Truck className="w-3 h-3" /> {stats.staff} {t('ao_pipeline_staff')}
          </span>
          <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
        </div>
      </div>
    </Link>
  );
}
